import { Box, Tooltip, useTheme } from "@material-ui/core";
import { CalendarToday, Chat } from "@material-ui/icons";
import { styled, keyframes, Badge } from "@mui/material";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useLogin } from "../../auth/hooks/useLogin";
import { useLogout } from "../../auth/hooks/useLogout";
import ModalCalendar from "../components/ModalCalendar";
import ModalChat from "../components/ModalChat";
import ModalSMS from "../components/ModalSMS";
import ModalAgnesAI from "../components/ModalAgnesAI";
import { Pessoal } from "../hooks/@types/candidateTypes";
import { useChat } from "../contexts/ChatProvider";

// Animações
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(25, 118, 210, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-3px);
  }
  100% {
    transform: translateY(0px);
  }
`;

// Styled Components
const FloatingButton = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: "50%",
  cursor: "pointer",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  animation: `${float} 3s ease-in-out infinite`,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  boxShadow: "0 4px 15px rgba(25, 118, 210, 0.3)",
  "&:hover": {
    transform: "scale(1.1) translateY(-2px)",
    boxShadow: "0 6px 20px rgba(25, 118, 210, 0.4)",
    animation: `${pulse} 1.5s infinite`,
  },
  "&:active": {
    transform: "scale(0.95)",
  },
  "& svg": {
    color: "#fff",
    fontSize: 22,
    transition: "transform 0.3s ease",
  },
  "&:hover svg": {
    transform: "rotate(10deg)",
  },
}));

const FloatingButtonChat = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: "50%",
  cursor: "pointer",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  animation: `${float} 3s ease-in-out infinite`,
  animationDelay: "0.3s",
  background: `linear-gradient(135deg, #7b1fa2 0%, #4a148c 100%)`,
  boxShadow: "0 4px 15px rgba(123, 31, 162, 0.3)",
  "&:hover": {
    transform: "scale(1.1) translateY(-2px)",
    boxShadow: "0 6px 20px rgba(123, 31, 162, 0.4)",
    animation: `${pulse} 1.5s infinite`,
  },
  "&:active": {
    transform: "scale(0.95)",
  },
  "& svg": {
    color: "#fff",
    fontSize: 22,
    transition: "transform 0.3s ease",
  },
  "&:hover svg": {
    transform: "rotate(-10deg)",
  },
}));

const FloatingButtonAI = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: "50%",
  cursor: "pointer",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  animation: `${float} 3s ease-in-out infinite`,
  animationDelay: "0.5s",
  backgroundSize: "cover !important",
  backgroundPosition: "center !important",
  boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
  border: `2px solid ${theme.palette.primary.light}`,
  "&:hover": {
    transform: "scale(1.1) translateY(-2px)",
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.3)",
    borderColor: theme.palette.primary.main,
  },
  "&:active": {
    transform: "scale(0.95)",
  },
}));

interface SmsContextInterface {
  isLoggingIn: boolean;
  isLoggingOut: boolean;
  setCanInfoFunc: (value: Pessoal) => void;
  canInfoContext: Pessoal | undefined;
}

export const SmsContext = createContext({} as SmsContextInterface);

type SmsProviderProps = {
  children?: React.ReactNode;
};

const SmsProvider = ({ children }: SmsProviderProps) => {
  const theme = useTheme();
  //@ts-ignore
  const [zoom, setZoom] = React.useState<string>("100%");
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const [modalOpenCalendar, setModalOpenCalendar] =
    React.useState<boolean>(false);
  const [modalOpenChat, setModalOpenChat] = React.useState<boolean>(false);

  const [modalOpenAI, setModalOpenAI] = React.useState<boolean>(false);

  const { isLoggingIn, login } = useLogin();
  const { isLoggingOut, logout } = useLogout();
  //@ts-ignore
  const initialZoomValue = document.body.style.zoom;

  // Obter hooks do ChatProvider
  const { totalUnreadCount, pendingSessionToOpen, setActiveSession } =
    useChat();

  // Observar pendingSessionToOpen e abrir modal quando necessário
  useEffect(() => {
    if (pendingSessionToOpen) {
      if (!modalOpenChat) {
        setModalOpenChat(true);
        // Aguardar modal abrir antes de ativar sessão
        setTimeout(() => {
          setActiveSession(pendingSessionToOpen);
        }, 150);
      } else {
        setActiveSession(pendingSessionToOpen);
      }
    }
  }, [pendingSessionToOpen, modalOpenChat, setActiveSession]);

  useEffect(() => {
    const getLocalZoom = localStorage.getItem("rh-zoom");
    if (!getLocalZoom) {
      localStorage.setItem("rh-zoom", "100%");
    } else {
      setZoom(getLocalZoom);
    }
    //@ts-ignore
    document.body.style.zoom = getLocalZoom;
  }, [zoom]);

  function hasCandidate(): boolean {
    const url = window.location.href;

    const pattern = /\/candidate\/\d+/;
    return pattern.test(url);
  }

  const zoomIn = () => {
    const getLocalZoom = localStorage.getItem("rh-zoom");
    const sub = getLocalZoom?.slice(0, -1);
    const subtraction = Number(sub) - 1;
    if (Number(sub) >= 81) {
      localStorage.setItem("rh-zoom", `${subtraction}%`);
      //@ts-ignore
      document.body.style.zoom = getLocalZoom;
      setZoom(`${subtraction}%`);
    }
  };

  const zoom100 = () => {
    const getLocalZoom = localStorage.getItem("rh-zoom");
    localStorage.setItem("rh-zoom", `100%`);
    //@ts-ignore
    document.body.style.zoom = getLocalZoom;
    setZoom(`100%`);
  };

  const zoomOut = () => {
    const getLocalZoom = localStorage.getItem("rh-zoom");
    const sub = getLocalZoom?.slice(0, -1);
    const subtraction = Number(sub) + 1;
    if (Number(sub) <= 190) {
      localStorage.setItem("rh-zoom", `${subtraction}%`);
      //@ts-ignore
      document.body.style.zoom = getLocalZoom;
      setZoom(`${subtraction}%`);
    }
  };

  const [canInfoContext, setCanInfoContext] = React.useState<
    Pessoal | undefined
  >();

  function setCanInfoFunc(value: Pessoal) {
    setCanInfoContext(value);
  }

  return (
    <SmsContext.Provider
      value={{
        isLoggingIn,
        isLoggingOut,

        setCanInfoFunc,
        canInfoContext,
      }}
    >
      <ModalSMS
        onClose={() => setModalOpen(!modalOpen)}
        open={modalOpen}
        title="Central de SMS"
        processing={false}
      />
      <ModalCalendar
        onClose={() => setModalOpenCalendar(!modalOpenCalendar)}
        open={modalOpenCalendar}
        title="Agenda"
        processing={false}
      />
      <ModalChat
        onClose={() => setModalOpenChat(false)}
        open={modalOpenChat}
        title="Chat"
        processing={false}
      />
      {/*  //@ts-ignore */}
      <ModalAgnesAI
        onClose={() => setModalOpenAI(!modalOpenAI)}
        open={modalOpenAI}
        title="Agnes IA"
        processing={false}
        canInfo={canInfoContext}
      />

      <div
        style={{
          position: "fixed",
          bottom: "20px",
          left: "20px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "10px",
          zIndex: 999,
          borderRadius: "28px",
          backdropFilter: "blur(10px)",
          backgroundColor: "rgba(255, 255, 255, 0.1)",
        }}
      >
        <Tooltip title="Agenda" placement="top" arrow disableInteractive>
          <FloatingButton
            onClick={() => setModalOpenCalendar(!modalOpenCalendar)}
          >
            <CalendarToday />
          </FloatingButton>
        </Tooltip>

        <Tooltip title="Chat" placement="top" arrow disableInteractive>
          <Badge
            badgeContent={totalUnreadCount}
            color="error"
            max={99}
            sx={{
              "& .MuiBadge-badge": {
                right: 8,
                top: 8,
                border: "2px solid white",
                padding: "0 4px",
                fontSize: "0.75rem",
                minWidth: "20px",
                height: "20px",
              },
            }}
          >
            <FloatingButtonChat onClick={() => setModalOpenChat(true)}>
              <Chat />
            </FloatingButtonChat>
          </Badge>
        </Tooltip>

        <Tooltip title="Agnes AI" placement="top" arrow disableInteractive>
          <FloatingButtonAI
            onClick={() => setModalOpenAI(!modalOpenAI)}
            sx={{
              background:
                "url(https://main.d1xvbis48sm8yx.amplifyapp.com/_next/static/media/ruti.5e914a94.png) center",
            }}
          />
        </Tooltip>
      </div>
      {children}
    </SmsContext.Provider>
  );
};

export function useSms() {
  return useContext(SmsContext);
}

export default SmsProvider;
